import { Outlet, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react'
import RecruiterHeaderNew from '@/components/common/RecruiterHeaderNew'
import InterviewScheduleModal from '@/components/interview/InterviewScheduleModal'
import CreatePositionModal from '@/components/positions/CreatePositionModal'
import { BUILD_VERSION } from '@/utils/buildVersion'

export default function RecruiterLayout() {
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showCreatePositionModal, setShowCreatePositionModal] = useState(false)
  const [modalScheduleData, setModalScheduleData] = useState<{candidateId?: string; candidateName?: string; positionId?: string; positionData?: any}>({})
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  const handleScheduleInterview = () => {
    setShowScheduleModal(true)
  }

  const handleCreatePosition = () => {
    setShowCreatePositionModal(true)
  }

  // Check authentication on mount
  useEffect(() => {
    const checkAuthentication = () => {
      // Add a small delay to ensure localStorage is available in static exports
      setTimeout(() => {
        const token = localStorage.getItem('token')
        const currentUser = localStorage.getItem('currentUser')
        
        if (token && currentUser) {
          setIsAuthenticated(true)
          setIsLoading(false)
        } else {
          // Redirect to login page with returnUrl to preserve the original path
          setIsLoading(false)
          const currentPath = window.location.pathname + window.location.search
          const loginUrl = `/login?returnUrl=${encodeURIComponent(currentPath)}`
          navigate(loginUrl)
        }
      }, 100)
    }

    // Only run auth check on client side
    if (typeof window !== 'undefined') {
      checkAuthentication()
    }
  }, [navigate])

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      return // Don't set up event listeners if not authenticated
    }

    // Listen for custom events from child components
    const handleOpenCreatePositionModal = () => {
      setShowCreatePositionModal(true)
    }

    const handleOpenScheduleModal = (event: any) => {
      const detail = event.detail || {}
      setModalScheduleData({
        candidateId: detail.candidateId,
        candidateName: detail.candidateName,
        positionId: detail.positionId,
        positionData: detail.positionData
      })
      setShowScheduleModal(true)
    }

    window.addEventListener('openCreatePositionModal', handleOpenCreatePositionModal)
    window.addEventListener('openScheduleModal', handleOpenScheduleModal)

    return () => {
      window.removeEventListener('openCreatePositionModal', handleOpenCreatePositionModal)
      window.removeEventListener('openScheduleModal', handleOpenScheduleModal)
    }
  }, [isAuthenticated, isLoading])

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    )
  }

  // Don't render anything if not authenticated (will redirect to login)
  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <RecruiterHeaderNew 
        onScheduleInterview={handleScheduleInterview}
        onCreatePosition={handleCreatePosition}
      />
      
      <main className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex-1 w-full">
        <Outlet />
      </main>

      <footer className="border-t border-gray-200 bg-white py-3 text-center text-xs text-gray-500">
        Build {BUILD_VERSION}
      </footer>

      {/* Modals */}
      <InterviewScheduleModal 
        open={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false)
          setModalScheduleData({})
        }}
        candidateId={modalScheduleData.candidateId}
        candidateName={modalScheduleData.candidateName}
        positionId={modalScheduleData.positionId}
        positionData={modalScheduleData.positionData}
      />
      
      <CreatePositionModal 
        open={showCreatePositionModal}
        onClose={() => setShowCreatePositionModal(false)}
        onSuccess={() => {
          // Dispatch event to notify pages to refresh their data
          window.dispatchEvent(new CustomEvent('positionCreated'))
          setShowCreatePositionModal(false)
        }}
      />
    </div>
  )
}
